/** Hook for the forgot-password flow and reset email submission. */
import { useCallback, useEffect, useState } from 'react';
import {
  getAuthErrorKey,
  sendPasswordResetEmailForUser,
} from '@/services/authService';
import { validateResetEmailField } from '@/features/auth/loginFormValidation';

const RESET_COOLDOWN_MS = 60_000;

export function usePasswordReset({ email, setEmail }) {
  const [fieldErrors, setFieldErrors] = useState({});
  const [serverErrorKey, setServerErrorKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [cooldownSecondsLeft, setCooldownSecondsLeft] = useState(0);

  const resetFormState = useCallback(() => {
    setFieldErrors({});
    setServerErrorKey(null);
    setSent(false);
    setCooldownUntil(0);
    setCooldownSecondsLeft(0);
  }, []);

  useEffect(() => {
    if (!cooldownUntil) {
      setCooldownSecondsLeft(0);
      return undefined;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
      setCooldownSecondsLeft(left);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [cooldownUntil]);

  const cooldownActive = cooldownSecondsLeft > 0;

  const runSendReset = async () => {
    if (loading || cooldownActive) {
      return;
    }
    const errors = validateResetEmailField(email);
    setFieldErrors(errors);
    setServerErrorKey(null);
    if (Object.keys(errors).length > 0) {
      return;
    }
    setLoading(true);
    try {
      await sendPasswordResetEmailForUser(email);
      setSent(true);
      setCooldownUntil(Date.now() + RESET_COOLDOWN_MS);
    } catch (error) {
      setServerErrorKey(getAuthErrorKey(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    void runSendReset();
  };

  const validateEmailBlur = () => {
    const errors = validateResetEmailField(email);
    setFieldErrors((prev) => ({
      ...prev,
      email: errors.email,
    }));
  };

  return {
    email,
    setEmail,
    fieldErrors,
    serverErrorKey,
    loading,
    sent,
    cooldownActive,
    cooldownSecondsLeft,
    resetFormState,
    handleSubmit,
    validateEmailBlur,
  };
}
